import { useEffect, useId, useMemo, useRef, useState } from "react";
import brandMark from "../../shared/brand-mark.png";
import {
  ClaimApiError,
  lookupHumanCode,
  lookupQrToken,
  redeemClaim,
  requestClaimSms,
  type ClaimSession,
  type RedeemResult,
  type SmsChallenge,
} from "./api";
import "./styles.css";

type Step = "code" | "phone" | "verify" | "complete";

const steps: Array<{ id: Step; title: string }> = [
  { id: "code", title: "领取码" },
  { id: "phone", title: "手机号" },
  { id: "verify", title: "验证" },
  { id: "complete", title: "完成" },
];

const errorCopy: Record<string, string> = {
  CLAIM_NOT_FOUND: "没有找到这个领取码，请检查字符后重试。",
  CLAIM_ALREADY_REDEEMED: "这个领取码已经使用。你可以直接在 App 登录并同步权益。",
  CLAIM_EXPIRED: "领取码已超过 30 天有效期，请联系订单客服重新签发。",
  ORDER_REFUNDED: "对应订单已经退款，当前无法领取。",
  ORDER_PHONE_MISMATCH: "手机号与订单预留信息不一致，请检查后重试。",
  SMS_CODE_INVALID: "验证码不正确，请重新输入。",
  SMS_EXPIRED: "验证码已过期，请重新获取。",
  RATE_LIMITED: "操作过于频繁，请稍后再试。",
};

function displayError(error: unknown): { message: string; requestId?: string } {
  if (error instanceof ClaimApiError) {
    return { message: errorCopy[error.code] ?? error.message, requestId: error.requestId };
  }
  return { message: "网络暂时不可用，请检查连接后重试。" };
}

function formatCode(value: string): string {
  const compact = value.toUpperCase().replace(/[^A-Z0-9]/gu, "");
  if (compact === "K") return "K";
  const body = (compact.startsWith("KH") ? compact.slice(2) : compact).slice(0, 8);
  return `KH-${body.slice(0, 4)}${body.length > 4 ? `-${body.slice(4)}` : ""}`;
}

function StepProgress({ current }: { current: Step }) {
  const currentIndex = steps.findIndex((step) => step.id === current);
  return (
    <ol className="step-progress" aria-label={`领取进度，第 ${currentIndex + 1} 步，共 4 步`}>
      {steps.map((step, index) => (
        <li key={step.id} className={index <= currentIndex ? "is-reached" : ""} aria-current={step.id === current ? "step" : undefined}>
          <span className="step-number">{index + 1}</span>
          <span>{step.title}</span>
        </li>
      ))}
    </ol>
  );
}

export default function App() {
  const codeId = useId();
  const phoneId = useId();
  const otpId = useId();
  const errorRef = useRef<HTMLDivElement | null>(null);
  const [step, setStep] = useState<Step>("code");
  const [humanCode, setHumanCode] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [claim, setClaim] = useState<ClaimSession | null>(null);
  const [sms, setSms] = useState<SmsChallenge | null>(null);
  const [result, setResult] = useState<RedeemResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; requestId?: string } | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const automaticLookup = useRef(false);
  const numericPhone = phone.replace(/\D/gu, "").slice(0, 11);
  const normalizedPhone = numericPhone.length === 11 ? `+86${numericPhone}` : "";
  const canSubmitCode = /^KH-[A-HJ-NP-Z2-9]{4}-[A-HJ-NP-Z2-9]{4}$/u.test(humanCode);
  const title = useMemo(() => {
    switch (step) {
      case "code": return "领取你的知识库";
      case "phone": return "验证订单手机号";
      case "verify": return "输入短信验证码";
      case "complete": return "领取成功";
    }
  }, [step]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = window.setTimeout(() => setResendCooldown((seconds) => Math.max(0, seconds - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [resendCooldown]);

  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("token");
    if (!token || automaticLookup.current) return;
    automaticLookup.current = true;
    setBusy(true);
    lookupQrToken(token)
      .then((session) => {
        setClaim(session);
        setStep("phone");
        window.history.replaceState({}, "", window.location.pathname);
      })
      .catch((caught) => setError(displayError(caught)))
      .finally(() => setBusy(false));
  }, []);

  async function submitCode(event: React.FormEvent) {
    event.preventDefault();
    if (!canSubmitCode || busy) return;
    setBusy(true);
    setError(null);
    try {
      const session = await lookupHumanCode(humanCode);
      setClaim(session);
      setStep("phone");
    } catch (caught) {
      setError(displayError(caught));
    } finally {
      setBusy(false);
    }
  }

  async function submitPhone(event: React.FormEvent) {
    event.preventDefault();
    if (!claim || !normalizedPhone || busy) return;
    setBusy(true);
    setError(null);
    try {
      const challenge = await requestClaimSms(claim.id, normalizedPhone);
      setSms(challenge);
      setResendCooldown(Math.max(1, challenge.retryAfter || 60));
      setStep("verify");
    } catch (caught) {
      setError(displayError(caught));
    } finally {
      setBusy(false);
    }
  }

  async function resendSms() {
    if (!claim || !normalizedPhone || busy || resendCooldown > 0) return;
    setBusy(true);
    setError(null);
    try {
      const challenge = await requestClaimSms(claim.id, normalizedPhone);
      setSms(challenge);
      setOtp("");
      setResendCooldown(Math.max(1, challenge.retryAfter || 60));
    } catch (caught) {
      setError(displayError(caught));
    } finally {
      setBusy(false);
    }
  }

  async function submitOtp(event: React.FormEvent) {
    event.preventDefault();
    if (!claim || !sms || otp.length !== 6 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const redemption = await redeemClaim(claim.id, sms.smsChallengeId, otp);
      setResult(redemption);
      setStep("complete");
    } catch (caught) {
      setError(displayError(caught));
    } finally {
      setBusy(false);
    }
  }

  function goBack() {
    setError(null);
    if (step === "verify") setStep("phone");
    if (step === "phone") {
      setClaim(null);
      setStep("code");
    }
  }

  return (
    <main className="claim-page">
      <header className="brand-header">
        <img src={brandMark} alt="" className="brand-mark" />
        <p className="brand-name">知识锤子</p>
        <p className="brand-promise"><span />知识锤子，锤出真知<span /></p>
      </header>

      <section className="claim-card" aria-labelledby="claim-title" aria-busy={busy}>
        <div className="card-heading">
          {step !== "code" && step !== "complete" ? (
            <button type="button" className="back-button" onClick={goBack} aria-label="返回上一步">←</button>
          ) : null}
          <p className="eyebrow">站外订单权益领取</p>
          <h1 id="claim-title">{title}</h1>
          <p className="intro">
            {step === "code" && "输入订单对应的领取码，验证手机号后即可同步到 App。"}
            {step === "phone" && `${claim?.knowledgeBase.name ?? "知识库"} · ${claim?.knowledgeBase.updateFrequency ?? "持续更新"}`}
            {step === "verify" && `验证码已发送至 +86 ${numericPhone.slice(0, 3)} **** ${numericPhone.slice(-4)}`}
            {step === "complete" && "你的订阅权益已经绑定到手机号，可在知识锤子 App 登录同步。"}
          </p>
        </div>

        <StepProgress current={step} />

        {error ? (
          <div className="error-banner" role="alert" ref={errorRef} tabIndex={-1}>
            <strong>暂时无法继续</strong>
            <span>{error.message}</span>
            {error.requestId ? <small>请求编号：{error.requestId}</small> : null}
          </div>
        ) : null}

        {step === "code" ? (
          <form onSubmit={submitCode} className="claim-form">
            <label htmlFor={codeId}>输入领取码</label>
            <div className="code-input-row">
              <input
                id={codeId}
                value={humanCode}
                onChange={(event) => setHumanCode(formatCode(event.target.value))}
                placeholder="KH-XXXX-XXXX"
                autoCapitalize="characters"
                autoComplete="off"
                inputMode="text"
                maxLength={12}
                aria-describedby={`${codeId}-hint`}
              />
              <button
                type="button"
                className="scan-button"
                onClick={() => setError({ message: "请使用手机系统相机扫描订单二维码；打开链接后会自动进入手机号验证。" })}
              >
                <span aria-hidden="true">⌗</span> 扫码领取
              </button>
            </div>
            <p id={`${codeId}-hint`} className="field-hint">字母不含 I、O，数字不含 0、1</p>
            <button className="primary-button" disabled={!canSubmitCode || busy}>
              {busy ? "正在核验…" : "继续领取"}
            </button>
          </form>
        ) : null}

        {step === "phone" ? (
          <form onSubmit={submitPhone} className="claim-form">
            <div className="knowledge-summary">
              <span className="summary-mark" aria-hidden="true">锤</span>
              <div>
                <strong>{claim?.knowledgeBase.name}</strong>
                <p>{claim?.knowledgeBase.summary}</p>
              </div>
            </div>
            {claim?.maskedOrderPhone ? (
              <p className="phone-policy">请使用订单预留手机号 {claim.maskedOrderPhone}</p>
            ) : (
              <p className="phone-policy">首次验证成功的手机号将成为知识锤子主账号</p>
            )}
            <label htmlFor={phoneId}>中国大陆手机号</label>
            <div className="phone-input">
              <span>+86</span>
              <input
                id={phoneId}
                value={numericPhone}
                onChange={(event) => setPhone(event.target.value)}
                inputMode="tel"
                autoComplete="tel-national"
                placeholder="请输入 11 位手机号"
                maxLength={11}
              />
            </div>
            <button className="primary-button" disabled={!normalizedPhone || busy}>
              {busy ? "正在发送…" : "发送验证码"}
            </button>
          </form>
        ) : null}

        {step === "verify" ? (
          <form onSubmit={submitOtp} className="claim-form">
            <label htmlFor={otpId}>6 位验证码</label>
            <input
              id={otpId}
              className="otp-input"
              value={otp}
              onChange={(event) => setOtp(event.target.value.replace(/\D/gu, "").slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              maxLength={6}
              autoFocus
            />
            {import.meta.env.DEV && sms?.debugCode ? (
              <p className="sandbox-code">本地沙盒验证码：<button type="button" onClick={() => setOtp(sms.debugCode ?? "")}>{sms.debugCode}</button></p>
            ) : null}
            <p className="field-hint">
              验证码 5 分钟内有效，连续输错 5 次会被锁定。
              {resendCooldown > 0
                ? ` ${resendCooldown} 秒后可重新发送。`
                : " 没收到？"}
            </p>
            {resendCooldown <= 0 ? (
              <button type="button" className="resend-button" onClick={() => void resendSms()} disabled={busy}>
                {busy ? "正在发送…" : "重新发送验证码"}
              </button>
            ) : null}
            <button className="primary-button" disabled={otp.length !== 6 || busy}>
              {busy ? "正在发放权益…" : "确认并领取"}
            </button>
          </form>
        ) : null}

        {step === "complete" && result ? (
          <div className="success-panel">
            <span className="success-icon" aria-hidden="true">✓</span>
            <h2>{result.knowledgeBase.name}</h2>
            <dl>
              <div><dt>绑定账号</dt><dd>{result.maskedPhone}</dd></div>
              <div><dt>权益状态</dt><dd>有效至 {new Intl.DateTimeFormat("zh-CN", { dateStyle: "long" }).format(new Date(result.entitlement.endsAt))}</dd></div>
            </dl>
            <a className="primary-button button-link" href={result.appLink}>打开知识锤子 App</a>
            <p className="field-hint">没有安装 App？稍后安装后使用同一手机号登录即可同步。</p>
          </div>
        ) : null}

        <footer className="claim-security">
          <p><span aria-hidden="true">◆</span> 领取码仅可使用一次 · 签发后 30 天内有效</p>
          <a href="mailto:support@slofst.com">领取遇到问题？</a>
        </footer>
      </section>

      <footer className="site-footer">
        <p>专注知识的整理、阅读与传承</p>
        <nav aria-label="法律与帮助"><a href="/privacy">隐私政策</a><span>·</span><a href="/terms">用户协议</a></nav>
      </footer>
    </main>
  );
}
